import { useHireflixInviteToInterview } from '@features/applications/hooks/useHireflixInviteToInterview';
import { Application } from '@features/applications/types';
import { useUpdateEnterpriseUpdateCompany } from '@features/profile/hooks/useUpdateEnterpriseUpdateCompany';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import Videocam from '@mui/icons-material/Videocam';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { authContext } from '@staffedupusa/shared-components';
import { hireflixCta, hireflixCtaHover } from '@styles/colors';
import BundledEditor from '@utils/BundledEditor';
import dayjs from 'dayjs';
import { decode } from 'html-entities';
import { FC, useContext, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Editor as TinyMCEEditor } from 'tinymce';

import HireflixLowCreditModal from '../HireflixLowCreditModal/HireflixLowCreditModal';
import HireflixPlanPurchaseModal from '../HireflixPlanPurchaseModal/HireflixPlanPurchaseModal';
import HireflixPositionNotFoundModal from '../HireflixPositionNotFoundModal/HireflixPositionNotFoundModal';

type HireflixInterviewModalProps = {
  application: Application;
  isOpen: boolean;
  handleClose: () => void;
  subtitle: string;
  selectedCompanyId?: string | number;
  jobId?: string | number;
};

const HireflixInterviewModal: FC<HireflixInterviewModalProps> = ({
  application,
  isOpen,
  handleClose,
  subtitle,
  jobId,
  selectedCompanyId,
}) => {
  const { authData } = useContext(authContext);
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const [errorText, setErrorText] = useState('');
  const [hireflixPlanModal, setHireflixPlanModal] = useState(false);
  const [hireflixPositionNotFoundModal, setHireflixPositionNotFoundModal] =
    useState(false);
  const [shouldSaveNewDefaultMessage, setShouldSaveNewDefaultMessage] =
    useState<boolean>(false);

  const DEFAULT_TRIAL_DAYS = 30;
  const hireflixPlan = authData?.user?.company?.hire_flix_plan;

  const planStatus = hireflixPlan?.status || 'Trial';
  const isTrial = !hireflixPlan || hireflixPlan?.status === 'Trial';
  const trialDaysLeft = hireflixPlan?.trial_start_date
    ? Math.max(
        0,
        DEFAULT_TRIAL_DAYS -
          dayjs().diff(dayjs(hireflixPlan.trial_start_date), 'day'),
      )
    : DEFAULT_TRIAL_DAYS;

  const isLowCredit =
    planStatus === 'Paid' && (hireflixPlan?.credit_limit || 0) <= 5;

  const [hireflixLowCreditModal, setHireflixLowCreditModal] =
    useState<boolean>(false);

  const [pendingInvitePayload, setPendingInvitePayload] = useState<any | null>(
    null,
  );

  const [message, setMessage] = useState<string>(
    authData?.user?.company?.default_hire_flix_interview_message,
  );
  const [initialMessage, setInitialMessage] = useState<string>(
    authData?.user?.company?.default_hire_flix_interview_message,
  );

  const normalize = (val: string = '') =>
    decode(val.replace(/<[^>]+>/g, '').trim());

  const isMessageEqual = normalize(message) === normalize(initialMessage);

  const { hireflixInviteToInterview, isHireflixInviteToInterviewLoading } =
    useHireflixInviteToInterview({ setErrorText });

  const { updateProfile, isUpdatingProfile } = useUpdateProfile({
    setErrorText,
    onSuccess: () => {
      handleClose();
    },
  });

  const { updateCompanyResponses, isUpdatingCompanyResponses } =
    useUpdateEnterpriseUpdateCompany({
      setErrorText,
      onSuccess: () => {
        handleClose();
      },
    });

  const handleSaveDefaultMessage = () => {
    if (message.length && shouldSaveNewDefaultMessage) {
      if (authData?.user?.company) {
        updateProfile({ default_hire_flix_interview_message: message });
      } else if (authData?.user?.enterprise) {
        updateCompanyResponses({
          default_hire_flix_interview_message: message,
          company_id: selectedCompanyId,
        });
      }
      setShouldSaveNewDefaultMessage(false);
    }
  };

  function sendInvite(payload: { application_ids: any[]; body: string }) {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data && data?.status === 200) {
          toast.success(
            data?.message || 'HireFlix interview invite sent successfully!',
          );
          setErrorText('');
          // Close the invite modal
          handleClose();
        } else {
          if (
            data?.message ===
            'No position found on HireFlix please create one before continuing.'
          ) {
            setHireflixPositionNotFoundModal(true);
          }
          setErrorText(
            data?.message ||
              'Unable to create interview invite. Please try again.',
          );
        }
      },
    };

    hireflixInviteToInterview({ ...payload }, options);
    setPendingInvitePayload(null);
  }

  const handleInviteToInterviewSubmit = (mesage: string) => {
    const payload = {
      application_ids: [application.id],
      body: mesage,
    };

    // If plan is paid, credits are low (<=5) and auto-refill is NOT enabled,
    // show confirmation modal before making the API call so the user can
    // choose to add credits first.
    const invitesCount = payload.application_ids.length;
    const creditsLeft = hireflixPlan?.credit_limit ?? 0;
    const autoRefill = hireflixPlan?.auto_refill;

    if (planStatus === 'Paid' && creditsLeft <= 5 && !autoRefill) {
      setPendingInvitePayload({ payload, mesage });
      setHireflixLowCreditModal(true);
      return;
    }

    // Otherwise send immediately
    sendInvite(payload);
  };

  const handleHireflixPlanModal = () => {
    setHireflixPlanModal(!hireflixPlanModal);
  };

  const handleHireflixPositionNotFoundModal = () => {
    setHireflixPositionNotFoundModal(!hireflixPositionNotFoundModal);
    setErrorText('');
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '64px',
            height: '64px',
            margin: '0 auto',
            borderRadius: '100%',
            backgroundColor: 'rgb(243, 232, 255)',
            mb: 2,
          }}
        >
          <Videocam sx={{ fontSize: 40, color: hireflixCta }} />
        </Box>
        <Typography variant="h4" sx={{ p: 3 }}>
          Send Hireflix Interview Invite
          <Typography>{subtitle}</Typography>
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            border: '1px solid rgb(233, 213, 255)',
            px: '10px',
            py: '10px',
            borderRadius: '10px',
            backgroundColor: 'rgb(250, 245, 255)',
            color: 'rgb(88, 28, 135)',
            textAlign: 'center',
          }}
        >
          <Typography sx={{}}>
            <strong style={{ color: 'rgb(126, 34, 206)' }}>
              HireFlix {planStatus}:
            </strong>{' '}
            {isTrial ? (
              trialDaysLeft > 0 ? (
                <i>
                  Unlimited recorded interviews –{' '}
                  <strong>{trialDaysLeft}</strong> days left on trial
                </i>
              ) : (
                <i>
                  Your HireFlix Trial has expired.{' '}
                  <Button
                    onClick={handleHireflixPlanModal}
                    style={{
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      color: 'rgb(126, 34, 206)',
                    }}
                  >
                    Manage Credit Plan
                  </Button>
                </i>
              )
            ) : (
              <i>
                You have <strong>{hireflixPlan?.credit_limit ?? 0}</strong>{' '}
                interview credits remaining
              </i>
            )}
          </Typography>
        </Box>

        <Stack spacing={3} sx={{ marginBottom: 4, mt: 2 }}>
          {errorText && <Alert severity="error">{errorText}</Alert>}

          <BundledEditor
            onInit={(evt: any, editor: TinyMCEEditor | null) =>
              (editorRef.current = editor)
            }
            value={message}
            onEditorChange={(newValue: string) => setMessage(newValue)}
            init={{
              height: 220,
              menubar: false,
              plugins: ['lists', 'autolink', 'link', 'pagebreak'],
              toolbar:
                'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
              content_style: 'textarea  { border-radius: 8px; }',
              spellchecker_dialog: true,
              spellchecker_rpc_url: 'localhost/ephox-spelling',
              spellchecker_language: 'en_US',
              spellchecker_active: true,
              browser_spellcheck: true,
              contextmenu: false,
            }}
          />

          {isMessageEqual && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={shouldSaveNewDefaultMessage}
                  onChange={(e) =>
                    setShouldSaveNewDefaultMessage(e.target.checked)
                  }
                />
              }
              label="Save as a default message"
            />
          )}

          <Stack
            spacing={2}
            width="100%"
            direction={{ xs: 'column', sm: 'row' }}
          >
            <LoadingButton
              onClick={() => {
                handleInviteToInterviewSubmit(message);
                if (shouldSaveNewDefaultMessage) {
                  handleSaveDefaultMessage();
                }
              }}
              sx={{
                width: '100%',
                backgroundColor: hireflixCta,
                '&:hover': {
                  backgroundColor: hireflixCtaHover,
                },
              }}
              variant="contained"
              color="secondary"
              disabled={
                isHireflixInviteToInterviewLoading ||
                isUpdatingProfile ||
                isUpdatingCompanyResponses
              }
              startIcon={<DoneIcon />}
            >
              Send Hireflix Interview Invite
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>

      {/* Hireflix Plan Purchase Modal */}
      {hireflixPlanModal && (
        <HireflixPlanPurchaseModal
          isOpen={hireflixPlanModal}
          handleClose={handleHireflixPlanModal}
        />
      )}

      {/* Hireflix Position Not Found Modal */}
      {hireflixPositionNotFoundModal && (
        <HireflixPositionNotFoundModal
          position={application?.position}
          isOpen={hireflixPositionNotFoundModal}
          handleClose={handleHireflixPositionNotFoundModal}
        />
      )}

      {hireflixLowCreditModal && (
        <HireflixLowCreditModal
          isOpen={hireflixLowCreditModal}
          handleClose={() => setHireflixLowCreditModal(false)}
          invitesCount={
            pendingInvitePayload?.payload?.application_ids?.length || 0
          }
          creditsLeft={hireflixPlan?.credit_limit ?? 0}
          onConfirmSend={() => {
            if (pendingInvitePayload?.payload) {
              sendInvite(pendingInvitePayload.payload);
            }
          }}
        />
      )}
    </Dialog>
  );
};

export default HireflixInterviewModal;
